import Image from "next/image";
import { useRouter } from "next/router";
import React, { ReactNode, useState } from "react";

import { CompareProducts } from "app/compare/CompareProducts";
import {
  canonical,
  displayModelName,
  smartphoneDetail,
  smartphoneEntry,
} from "app/compare/utils";
import BreadCrumb from "shared/BreadCrumb";
import { AppLayout } from "shared/Layout";
import { fetchSettings } from "shared/SettingsProvider/settings.helper";
import { getAvailableEntries } from "shared/catalog/AvailableCatalog/available-catalog.helper";
import { SmartphoneFamilyDescription } from "shared/product/ProductSection/product-section.helper";
import { Loader } from "ui/Loader";
import { useRouteChange } from "utils/hooks/useRouteChange";
import { withApp } from "utils/with-app.utils";
import { fetchTranslations, useI18n } from "utils/with-i18n.utils";
import { fetchSEO, withSeo } from "utils/with-seo.utils";

import { BestsComparedSection } from "./BestsComparedSection";
import { getComparatorData, getComparatorWinner } from "./ComparatorHelper";
import { ModelsSelector } from "./ModelsSelector";

export type ComparedModel = {
  slug: string;
  description: SmartphoneFamilyDescription | null;
  product:
    | NonNullable<Awaited<ReturnType<typeof getAvailableEntries>>>[number]
    | null;
};

type CompareProps = NonNullable<
  Awaited<ReturnType<typeof getServerSideProps>>["props"]
>;

export const Compare = ({
  leftComparedModel,
  rightComparedModel,
  slugs,
  winner,
}: CompareProps) => {
  const i18n = useI18n();
  const router = useRouter();

  const [prevSlugs, setPrevSlugs] = useState(slugs);
  const [leftSlug, setLeftSlug] = useState(leftComparedModel?.slug);
  const [rightSlug, setRightSlug] = useState(rightComparedModel?.slug);
  const { loading } = useRouteChange();

  if (prevSlugs !== slugs) {
    setPrevSlugs(slugs);
    setLeftSlug(leftComparedModel?.slug);
    setRightSlug(rightComparedModel?.slug);

    return null;
  }

  if (
    leftSlug &&
    rightSlug &&
    (leftSlug !== leftComparedModel?.slug ||
      rightSlug !== rightComparedModel?.slug)
  ) {
    const comparePath = `/compare/${leftSlug}-vs-${rightSlug}`;

    router.replace(comparePath);
  }

  return (
    <div className="flex flex-col gap-8 px-5 mb-10 md:gap-10">
      <BreadCrumb
        parts={
          leftSlug && rightSlug
            ? [
                { label: i18n.t("breadcrumb.home"), to: "/" },
                { label: i18n.t("breadcrumb.compare"), to: "/compare" },
                {
                  label: `${displayModelName(leftSlug)} vs ${displayModelName(
                    rightSlug,
                  )}`,
                  to: "",
                },
              ]
            : [
                { label: i18n.t("breadcrumb.home"), to: "/" },
                { label: i18n.t("breadcrumb.compare"), to: "/compare" },
              ]
        }
      />

      {/* Title */}
      <h1 className="mx-auto w-fit text-2xl font-medium text-center lg:mt-5 lg:!text-5xl 2xs:text-3xl">
        {leftSlug && rightSlug
          ? `${displayModelName(leftSlug)} vs ${displayModelName(rightSlug)}`
          : i18n.t("comparator.title")}
      </h1>

      {/* Sticky models selectors */}
      <ModelsSelector
        className="max-w-[950px]"
        slugs={slugs}
        leftSlug={leftSlug}
        rightSlug={rightSlug}
        setLeftModel={setLeftSlug}
        setRightModel={setRightSlug}
      />

      {loading ? (
        <div className="m-auto">
          <Loader />
        </div>
      ) : (
        <CompareProducts
          left={leftComparedModel}
          right={rightComparedModel}
          winner={winner}
        />
      )}

      {(!leftSlug || !rightSlug) && (
        <section className="flex flex-col-reverse gap-14 items-center py-8 px-5 mx-auto w-full max-w-[1200px] bg-gradient-to-tr from-[#B492FF] to-[#4461C8] rounded-md md:flex-row md:px-14">
          <Image
            src="/images/comparator/comparator.webp"
            alt="compare"
            width={341}
            height={190}
          />

          <div className="text-white md:w-2/3">
            <h2 className="mb-3 text-2xl font-bold">
              {i18n.t("comparator.subtitle")}
            </h2>
            <p className="text-lg leading-snug">
              {i18n.t("comparator.text", {}, { html: true })}
            </p>
          </div>
        </section>
      )}

      {/* Best matches */}
      <div className="flex flex-col gap-10 my-10">
        <BestsComparedSection />
      </div>
    </div>
  );
};

Compare.getLayout = (page: ReactNode, pageProps: CompareProps) => (
  <AppLayout withBanner {...pageProps}>
    {page}
  </AppLayout>
);

export const getServerSideProps = async ({
  params,
}: {
  params?: { match: string };
}) => {
  const { slugs } = await getComparatorData();

  const translations = await fetchTranslations([
    "marquee",
    "breadcrumb",
    "comparator",
  ]);

  const seo = await fetchSEO("compare");

  if (!params?.match) {
    return {
      props: {
        translations,
        seo,
        slugs,
        settings: await fetchSettings(),
      },
    };
  }

  const [leftSlug, rightSlug] = params.match.split("-vs-");
  const { winner } = await getComparatorWinner(leftSlug, rightSlug);

  const leftModelDetail = await smartphoneDetail(leftSlug);
  const rightModelDetail = await smartphoneDetail(rightSlug);

  if (!leftModelDetail || !rightModelDetail) {
    return {
      redirect: {
        destination: "/compare",
      },
    };
  }

  const leftComparedModel: ComparedModel = {
    slug: leftSlug,
    description: leftModelDetail.description,
    product: await smartphoneEntry(leftSlug),
  };

  const rightComparedModel: ComparedModel = {
    slug: rightSlug,
    description: rightModelDetail.description,
    product: await smartphoneEntry(rightSlug),
  };

  const [leftModelDisplayName, rightModelDisplayName] = [
    leftSlug,
    rightSlug,
  ].map((model) => displayModelName(model));

  return {
    props: {
      translations,
      seo: {
        ...seo,
        metaTitle: `${leftModelDisplayName} vs ${rightModelDisplayName} : notre comparaison`,
        metaDescription: `Avec notre comparateur, faites le meilleur choix de location entre ${leftModelDisplayName} et ${rightModelDisplayName}`,
        canonical: canonical(leftSlug, rightSlug),
      },
      slugs,
      leftComparedModel,
      rightComparedModel,
      winner,
      settings: await fetchSettings(),
    },
  };
};

export default withApp(Compare, [withSeo]);
